"use client"

import { PrintDataFields } from '@repo/types';
import { InventoryService } from '@repo/ui/lib/api/services/inventory.service';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

// Query key factory for print data (optional history snapshot)
export const printDataQueryKey = (id: number, historyId?: number) => [
  'inventaire',
  'print-data',
  id,
  historyId ?? null,
] as const;

export interface UsePrintDataOptions
  extends Omit<
    UseQueryOptions<
      PrintDataFields,
      Error,
      PrintDataFields,
      ReturnType<typeof printDataQueryKey>
    >,
    'queryKey' | 'queryFn'
  > {
  readonly historyId?: number;
}

/**
 * Hook for fetching print data for an inventaire by ID.
 * When `historyId` is set, values come from that history snapshot
 * (same backend pipeline as print arbre: GET /print-data/:id?historyId=).
 */
export function usePrintData(
  id: number | null | undefined,
  options?: UsePrintDataOptions,
) {
  const { historyId, ...queryOptions } = options ?? {};
  const inventoryService = new InventoryService();

  return useQuery({
    queryKey: printDataQueryKey(id!, historyId),
    queryFn: () => {
      if (!id) throw new Error('Inventaire ID is required');
      return inventoryService.getPrintData(id, historyId);
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    ...queryOptions,
  });
}
